import styled, { css } from 'styled-components';
import { StyledComponentsPropWithTheme } from 'src/commons/components/ChromeApp/ChromeAppUtils';
import {
  Button,
  Flex,
  Icon,
  isDarkThemeMixin,
  isMobileStyleMixin,
  Link,
  SearchInput,
  styleMixinFocusOutline,
  styleMixinInvertedFocusOutline,
  Text,
  TextButton,
} from '@amzn/storm-ui';

export const accountSelectionListId = `top-bar-utilities-acc-selector`;

interface AccountDropdownButtonProps extends StyledComponentsPropWithTheme {
  active?: boolean;
  status?: 'inactive' | 'active';
}

export const IA2AccountDropdownButton = styled(
  Button,
)<AccountDropdownButtonProps>`
  background-color: transparent;
  border: none;
  padding: ${({ theme }) =>
    `${theme.spacing.xxxsmall.default} ${theme.spacing.xxsmall.default}`};
  margin-block: ${({ theme }) => theme.spacing.xxxxsmall.default};

  ${isMobileStyleMixin(css`
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xxxsmall.mobile};
    justify-content: flex-start;
  `)};

  ${({ active }) =>
    active &&
    css`
      background-color: ${({ theme }) => theme.color.gray[800]};
    `};

  &&:disabled {
    background-color: transparent;
  }

  &&:hover,
  &&:focus {
    background-color: ${({ theme }) => theme.color.gray[1000]};
  }

  &&:focus-visible {
    ${styleMixinInvertedFocusOutline};
    ${isDarkThemeMixin(css`
      ${styleMixinFocusOutline};
    `)};
  }
`;

export const AccountName = styled.div`
  text-align: right;
  line-height: 16px;
  color: ${({ theme }) => theme.header.utility.icon.color};
`;

export const AccountDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 350px;
  height: initial;
  border-radius: ${({ theme }) => theme.border.radius.medium};
  overflow: hidden;

  /* Edge */
  @supports (-ms-ime-align: auto) {
    & {
      overflow-x: hidden;
    }
  }
`;

interface AccountsListWrapperProps {
  hasManagerAccounts: boolean;
}

/*
 * The account list wrapper's max height is limited to 398px,
 * if no manager accounts are present. The number is not determined
 * by a specific element.
 */
export const AccountsListWrapper = styled.div<AccountsListWrapperProps>`
  flex-grow: 1;
  height: initial;
  padding-bottom: 14px;

  ${(props) =>
    !props.hasManagerAccounts
      ? `
      max-height: 398px;
      overflow-y: auto;
    `
      : ''};
`;

export const IA2AccountDropdownList = styled.ul`
  margin: 0;
  text-align: left;
  padding: 0;

  ${isMobileStyleMixin(css`
    display: grid;
    gap: ${({ theme }) => theme.spacing.small.default};
  `)};

  &:empty ${AccountsListWrapper} {
    display: none;
  }
`;

export const IA2AccountDropdownSearchInput = styled(SearchInput)`
  margin-block-start: ${({ theme }) => theme.spacing.base.default};

  & input [type='search'] {
    border: none;
    outline: none;
    box-shadow: none;
    height: auto;
  }

  ${isMobileStyleMixin(css`
    margin-block-start: ${({ theme }) => theme.spacing.xxsmall.mobile};
  `)};
`;

export const AccountDropdownAccountName = styled(Text)`
  color: ${({ theme }) => theme.text.color.inverted};
  ${isMobileStyleMixin(css`
    white-space: initial;
    overflow-wrap: anywhere;
  `)};
  margin-bottom: -2px;

  ${isDarkThemeMixin(css`
    color: ${({ theme }) => theme.text.color.default};
  `)};
`;

export const IA2AccountListItem = styled.li<{
  isSelected: boolean;
}>`
  list-style: none;
  border-radius: ${({ theme }) => theme.border.radius.small};

  ${({ theme, isSelected }) =>
    isSelected && `background-color: ${theme.surface.offset};`};

  &:hover {
    background-color: ${({ theme }) => theme.surface.offset};
  }
`;

export const PaginationButton = styled(TextButton)<{
  extraPaddingAtBottom?: boolean;
}>`
  text-align: left;
  display: block;
  width: 100%;
  padding-inline: 1.25rem;
  margin-block: 0.25rem;

  ${({ extraPaddingAtBottom }) =>
    extraPaddingAtBottom &&
    css`
      padding-bottom: ${({ theme }) => theme.spacing.xsmall.default};
    `};

  ${isMobileStyleMixin(css`
    margin-top: ${({ theme }) => theme.spacing.xsmall.mobile};
  `)};
`;

export const AccountTypeLabel = styled(Text)<{ withMargin?: boolean }>`
  margin-block: 0;
  margin-inline: ${({ withMargin, theme }) =>
    withMargin === true ? theme.spacing.base.default : ''};
`;

export const AccountTypeLabelContainer = styled(Flex).attrs({
  alignItems: 'center',
})`
  white-space: nowrap;

  ${isMobileStyleMixin(css`
    margin-block: ${({ theme }) =>
      `${theme.spacing.xsmall.mobile} ${theme.spacing.xxsmall.mobile}`};
  `)};
`;

export const TooltipLink = styled(Link)`
  && {
    color: ${({ theme }) => theme.text.color.inverted};
    &:hover {
      color: ${({ theme }) => theme.text.color.inverted};
    }
  }
`;

export const MobileHelpTagWrapper = styled.span`
  display: inline-flex;
  margin-inline-start: ${({ theme }) => theme.spacing.xsmall.mobile};
  margin-block-end: ${({ theme }) => theme.spacing.xxxsmall.mobile};
`;

export const AlertContentContainer = styled.div`
  width: 180px;
  padding: ${({ theme }) => theme.spacing.xxsmall.default};
  white-space: normal;
  word-wrap: break-word;
  text-align: start;
  color: ${({ theme }) => theme.text.color.inverted};
`;

export const TooltipWrapper = styled.span`
  margin-inline-start: ${({ theme }) => theme.spacing.xxsmall.default};
`;

export const TooltipIcon = styled(Icon)`
  margin-inline-start: ${({ theme }) => theme.spacing.xxsmall.default};
  vertical-align: middle;
  color: ${({ theme }) => theme.icon.color.inverted};
`;

export const LinkContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxsmall.default};
  display: block;
`;
